/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * 검사 과정에서 사용자 클릭 이벤트를 로깅하는 유틸리티
 */

export type AgreementLevel = 'NN' | 'N' | 'A' | 'AA';
export type ClassificationStep = '1' | '2_1' | '2_2' | '2_3' | '2_4' | '3' | 'final' | 'additional' | 'survey';

export interface IBrowserInfo {
  userAgent?: string; // User Agent 문자열
  browserName?: string; // 브라우저 이름 (Chrome, Safari, Firefox 등)
  browserVersion?: string; // 브라우저 버전
  osName?: string; // 운영체제 이름 (Windows, iOS, Android 등)
  osVersion?: string; // 운영체제 버전
  deviceType?: string; // 디바이스 타입 (desktop, mobile, tablet)
  screenWidth?: number; // 화면 너비 (픽셀)
  screenHeight?: number; // 화면 높이 (픽셀)
  language?: string; // 브라우저 언어 설정 (ko, en 등)
  timezone?: string; // 타임존 (Asia/Seoul 등)
}

export interface IInspectionClickLog {
  // 기본 정보
  projectId: string;
  partCode: string;
  inspectionId: string;
  
  // 단계 정보
  step: ClassificationStep; // '1', '2_1', '2_2', '2_3', '2_4', '3', 'final', 'additional'
  stepName: string; // '1단계', '2단계 PART 1', '3단계', '최종 단계' 등
  
  // 클릭 정보
  actionType: 'agreement_click' | 'final_selection' | 'next_button' | 'prev_button' | 'survey_likert_click'; // 클릭 타입
  agreementLevel?: AgreementLevel; // 'NN', 'N', 'A', 'AA' (agreement_click인 경우)
  agreementLevelName?: string; // '전혀 동의하지 않음', '동의하지 않음', '동의함', '전적으로 동의함'
  
  // 문항 정보
  ipsativeId?: string; // 문항 ID
  ipsativeContent?: string; // 문항 내용 (개별진단 리커트: 척도에 표시된 번호·라벨, 보통 item.content)
  ipsativeIndex?: string; // 문항 순서 (형식: "현재번호/전체개수", 예: "2/7")

  // 개별진단 설문 리커트 (survey_likert_click)
  likertOptionIndex?: number; // 리커트 척도에서 선택한 보기 번호 (왼쪽부터 1부터)
  likertOptionId?: string; // 선택한 보기 항목 id
  
  // 최종 단계 정보
  selectedCount?: number; // 최종 단계에서 선택한 개수
  totalAvailable?: number; // 최종 단계에서 선택 가능한 총 개수
  
  // 메타 정보
  timestamp: string; // 클릭 시각 (ISO 8601)
  sessionId?: string; // 세션 ID (선택사항)
  
  // 브라우저 정보
  browserInfo?: IBrowserInfo;
}

/**
 * 브라우저 정보 수집 함수
 */
function getBrowserInfo(): IBrowserInfo {
  const userAgent = navigator.userAgent;
  const screen = window.screen;
  
  // 브라우저 감지
  let browserName = 'Unknown';
  let browserVersion = '';
  
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg') && !userAgent.includes('OPR')) {
    browserName = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browserName = 'Safari';
    const match = userAgent.match(/Version\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (userAgent.includes('Firefox')) {
    browserName = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (userAgent.includes('Edg')) {
    browserName = 'Edge';
    const match = userAgent.match(/Edg\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (userAgent.includes('OPR')) {
    browserName = 'Opera';
    const match = userAgent.match(/OPR\/(\d+)/);
    browserVersion = match ? match[1] : '';
  }
  
  // OS 감지
  let osName = 'Unknown';
  let osVersion = '';
  
  if (userAgent.includes('Windows')) {
    osName = 'Windows';
    const match = userAgent.match(/Windows NT (\d+\.\d+)/);
    osVersion = match ? match[1] : '';
  } else if (userAgent.includes('Mac OS X')) {
    osName = 'macOS';
    const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
    osVersion = match ? match[1].replace('_', '.') : '';
  } else if (userAgent.includes('iPhone')) {
    osName = 'iOS';
    const match = userAgent.match(/OS (\d+[._]\d+)/);
    osVersion = match ? match[1].replace('_', '.') : '';
  } else if (userAgent.includes('Android')) {
    osName = 'Android';
    const match = userAgent.match(/Android (\d+\.\d+)/);
    osVersion = match ? match[1] : '';
  } else if (userAgent.includes('Linux')) {
    osName = 'Linux';
  }
  
  // 디바이스 타입 감지
  let deviceType = 'desktop';
  if (/iPhone|iPad|iPod|Android/i.test(userAgent)) {
    deviceType = /iPad/i.test(userAgent) ? 'tablet' : 'mobile';
  }
  
  // 타임존
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  return {
    userAgent,
    browserName,
    browserVersion,
    osName,
    osVersion,
    deviceType,
    screenWidth: screen.width,
    screenHeight: screen.height,
    language: navigator.language || navigator.languages?.[0] || 'ko',
    timezone,
  };
}

// 로그를 메모리에 저장하고 배치로 전송
class InspectionLogger {
  private logs: IInspectionClickLog[] = [];
  private batchSize = 5; // 5개씩 모아서 전송 (더 빠른 전송)
  private flushInterval = 30000; // 30초마다 자동 전송 (컴포넌트에서 처리하므로 여기서는 사용 안 함)
  private flushTimer: NodeJS.Timeout | null = null;
  private sessionId: string;
  private browserInfo: IBrowserInfo;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.browserInfo = getBrowserInfo(); // 브라우저 정보는 한 번만 수집
    
    // 개발 환경에서 브라우저 정보 확인
    if (process.env.NODE_ENV === 'development') {
      console.log('[InspectionLogger] 브라우저 정보 수집:', this.browserInfo);
    }
    
    this.startAutoFlush();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private startAutoFlush(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flushTimer = setInterval(() => {
      if (this.logs.length > 0) {
        this.flush();
      }
    }, this.flushInterval);
  }

  /**
   * 클릭 이벤트 로그 추가
   */
  logClick(log: Omit<IInspectionClickLog, 'timestamp' | 'sessionId' | 'browserInfo'>): void {
    const fullLog: IInspectionClickLog = {
      ...log,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      browserInfo: this.browserInfo || getBrowserInfo(), // 브라우저 정보 자동 추가 (없으면 다시 수집)
    };

    this.logs.push(fullLog);

    // 배치 크기에 도달하면 즉시 전송
    if (this.logs.length >= this.batchSize) {
      this.flush();
    }

    // 개발 환경에서는 콘솔에도 출력
    if (process.env.NODE_ENV === 'development') {
      console.log('[InspectionLogger] 로그 추가:', {
        ...fullLog,
        browserInfo: fullLog.browserInfo,
      });
    }
  }

  /**
   * 로그를 서버로 전송 (반환)
   */
  async flush(): Promise<IInspectionClickLog[]> {
    if (this.logs.length === 0) return [];

    const logsToSend = [...this.logs];
    this.logs = []; // 전송할 로그를 복사한 후 배열 초기화

    return logsToSend;
  }

  /**
   * 남은 로그를 모두 전송하고 정리
   */
  async flushAll(): Promise<IInspectionClickLog[]> {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    return await this.flush();
  }

  /**
   * 로그 초기화
   */
  clear(): void {
    this.logs = [];
  }

  /**
   * 현재 저장된 로그 개수 반환
   */
  getLogCount(): number {
    return this.logs.length;
  }
}

// 싱글톤 인스턴스
export const inspectionLogger = new InspectionLogger();

/**
 * 동의 레벨을 한글 이름으로 변환
 */
export function getAgreementLevelName(level: AgreementLevel): string {
  const mapping: Record<AgreementLevel, string> = {
    NN: '전혀 동의하지 않음',
    N: '동의하지 않음',
    A: '동의함',
    AA: '전적으로 동의함',
  };
  return mapping[level] || level;
}

/**
 * 단계를 한글 이름으로 변환
 */
export function getStepName(step: ClassificationStep): string {
  const mapping: Record<string, string> = {
    '1': '1단계',
    '2_1': '2단계 PART 1',
    '2_2': '2단계 PART 2',
    '2_3': '2단계 PART 3',
    '2_4': '2단계 PART 4',
    '3': '3단계',
    'final': '최종 단계',
    'additional': '추가 설문',
    survey: '개별진단 설문',
  };
  return mapping[step] || step;
}
